import React, { forwardRef, lazy, memo, Suspense, useEffect, useImperativeHandle, useState } from 'react'
import { SeriesGroup } from '../chart.typings'

// The menu's option tree (and the strategy parser it pulls in) is fetched on the first
// right-click rather than shipped in the entry bundle.
const ChartContextMenuOptions = lazy(() => import('./chartContextMenuOptions'))

export type ChartContextMenuSource = 'chart' | 'screener'

export type ChartContextMenuAnchor = {
    x: number
    y: number
    price: string
    position: 'left' | 'right'
    seriesGroup: SeriesGroup | undefined
    anchorOriginHorizontal?: 'left' | 'right'
    transformOriginHorizontal?: 'left' | 'right'
    /** Row / surface that opened the menu; defaults to chart right-click. */
    source?: ChartContextMenuSource
}

export type ChartContextMenuHostHandle = {
    open: (anchor: ChartContextMenuAnchor) => void
}

const CLOSED_ANCHOR: ChartContextMenuAnchor = {
    x: 0,
    y: 0,
    price: '',
    position: 'left',
    seriesGroup: undefined
}

export const CHART_CONTEXT_MENU_ATTR = 'data-chart-context-menu'
export const CHART_CONTEXT_MENU_CHILD_ATTR = 'data-chart-context-menu-child'
export const CHART_CONTEXT_MENU_SELECTOR = `[${CHART_CONTEXT_MENU_ATTR}], [${CHART_CONTEXT_MENU_CHILD_ATTR}]`

type Props = {
    viewKey: string
}

const ChartContextMenuHost = forwardRef<ChartContextMenuHostHandle, Props>(function ChartContextMenuHost(
    { viewKey },
    ref
) {
    const [anchorPosition, setAnchorPosition] = useState<ChartContextMenuAnchor>(CLOSED_ANCHOR)
    // Same open test as chartContextMenuOptions; once opened it stays mounted so closing animates.
    const [mounted, setMounted] = useState(false)
    useEffect(() => {
        if (anchorPosition.x !== 0 && anchorPosition.y !== 0) setMounted(true)
    }, [anchorPosition.x, anchorPosition.y])

    useImperativeHandle(
        ref,
        () => ({
            open: setAnchorPosition
        }),
        []
    )

    if (!mounted) return null
    return (
        <Suspense fallback={null}>
            <ChartContextMenuOptions
                anchorPosition={anchorPosition}
                setAnchorPosition={setAnchorPosition}
                viewKey={viewKey}
            />
        </Suspense>
    )
})

export default memo(ChartContextMenuHost)
